import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { prefetchRoute } from '@/lib/prefetch';
import {
  Code,
  Zap,
  BookOpen,
  FlaskConical,
  LibraryBig,
  ChevronRight
} from 'lucide-react';

const ProductsHoverContainer = styled.div`
  width: 760px;
  background-color: transparent;
  border-radius: 12px;
  box-shadow: 5px 6px 12px 2px rgba(var(--greyr), 0.3);
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  overflow: hidden;
  background-color: var(--white);
  @media (max-width: 968px) {
    width: calc(100% - 40px);
    gap: 0;
    padding: 0;
    border-radius: 0;
    box-shadow: none;
    margin-bottom: 20px;
  }
`;

const TopRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const BottomRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const CardBase = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 16px;
  border-radius: 8px;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.15s;
  background-color: rgba(var(--greyr), 0.08);
  position: relative;
  overflow: hidden;
  background-image: repeating-linear-gradient(
    -45deg,
    transparent,
    transparent 4px,
    rgba(var(--greyr), 0.07) 4px,
    rgba(var(--greyr), 0.07) 5px
  );
  &.space{
    padding-bottom: 140px;
  }
  &:hover {
    background-color: rgba(var(--greyr), 0.14);
  }
  &:after {
    height: 0px !important;
    bottom: 0px !important;
  }

  @media (max-width: 968px) {
    gap: 12px;
margin: 0;
    background-color: white;
    display: flex!important;
    flex-direction: row;
    align-items: self-start!important;
    background-image: none;
    padding-left: 10px!important;
    &:hover{
      background-color: rgba(var(--greyr), 0.1);
    }
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconWrapper = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: ${({ $bg }) => $bg || 'rgba(var(--greyr), 0.2)'};
  svg {
    width: 15px;
    height: 15px;
    color: ${({ $iconColor }) => $iconColor || 'var(--grey)'};
  }


  @media (max-width: 968px) {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background-color: rgba(var(--greyr), 0.1);
    border: 1px solid rgba(var(--greyr), 0.12);
    svg{
     color: var(--grey);
    }
  }

}
`;

const CardMobileTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--black);
  display: flex;
  align-items: center;
  gap: 4px;
  display: none;

  @media (max-width: 968px) {
      display: block;
  }
`;
const CardTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--black);
  display: flex;
  align-items: center;
  gap: 4px;
  svg {
    width: 14px;
    height: 14px;
    color: var(--grey);
  }

  @media (max-width: 968px) {
      display: none;
  }
`;

const CardDescription = styled.p`
  font-size: 13px;
  color: var(--grey);
  margin: 0 0 10px 0;
  line-height: 1.4;
`;

const IllustrationPlaceholder = styled.div`

  position: absolute;
  width: auto;
  height: 140px;
  margin-top: auto;
  overflow: hidden;
  bottom: 0;
  right: 0;
  img{
    width: auto;
    max-height: 100%;
    object-fit: cover;
  }

  @media (max-width: 968px) {
    display: none;
  }
`;

const ProductsHover = () => {
  return (
    <ProductsHoverContainer>
      <TopRow>
        <CardBase to="/developers" onMouseEnter={() => prefetchRoute('/developers')} className="space">
          <CardTop>
            <IconWrapper $bg="rgba(79,115,245,0.15)" $iconColor="#4F73F5">
              <Zap />
            </IconWrapper>
            <CardTitle>Getting started <ChevronRight /></CardTitle>
          </CardTop>
          <div>
            <CardMobileTitle>Getting started </CardMobileTitle>
            <CardDescription>Make your first API call to live market data in minutes.</CardDescription>
          </div>
          <IllustrationPlaceholder > <img src="/nav/started.png" width={210} height={140} alt="Getting started with the AxionQuant API" loading="lazy"/> </IllustrationPlaceholder>
        </CardBase>

        <CardBase to="/developers/libraries" onMouseEnter={() => prefetchRoute('/developers/libraries')} className="space">
          <CardTop>
            <IconWrapper $bg="rgba(60,60,60,0.12)" $iconColor="var(--black)">
              <Code />
            </IconWrapper>
            <CardTitle>SDKs <ChevronRight /></CardTitle>
          </CardTop>

          <div>
            <CardMobileTitle>SDKs</CardMobileTitle>
            <CardDescription>Native libraries for Python, JavaScript, and more.</CardDescription>
          </div>

          <IllustrationPlaceholder > <img src="/nav/sdk.png" width={210} height={140} alt="AxionQuant SDKs for Python and JavaScript" loading="lazy"/> </IllustrationPlaceholder>
        </CardBase>

        <CardBase to="/docs" target="_blank" rel="noopener noreferrer" className="space">
          <CardTop>
            <IconWrapper $bg="rgba(0,180,180,0.15)" $iconColor="#00B4B4">
              <BookOpen />
            </IconWrapper>
            <CardTitle>Docs <ChevronRight /></CardTitle>
          </CardTop>

          <div>
            <CardMobileTitle>Docs</CardMobileTitle>
            <CardDescription>Full API reference, endpoint guides, and code examples.</CardDescription>
          </div>
          <IllustrationPlaceholder > <img src="/nav/docs.png" width={210} height={140} alt="Axion API documentation" loading="lazy"/> </IllustrationPlaceholder>
        </CardBase>
      </TopRow>

      <BottomRow>
        <CardBase to="/dashboard/playground">
          <CardTop>
            <IconWrapper $bg="rgba(79,115,245,0.1)" $iconColor="#4F73F5">
              <FlaskConical/>
            </IconWrapper>
            <CardTitle>Playground <ChevronRight /></CardTitle>
          </CardTop>

          <div>
            <CardMobileTitle>Playground</CardMobileTitle>
            <CardDescription>Experiment with the API, make sample requests, and inspect responses instantly.</CardDescription>
          </div>
        </CardBase>

        <CardBase to="/blog/" onMouseEnter={() => prefetchRoute('/blog')}>
          <CardTop>
            <IconWrapper $bg="rgba(60,60,60,0.08)" $iconColor="var(--grey)">
              <LibraryBig/>
            </IconWrapper>
            <CardTitle>Tutorials &amp; Guides <ChevronRight /></CardTitle>
          </CardTop>

          <div>
            <CardMobileTitle>Tutorials &amp; Guides</CardMobileTitle>
            <CardDescription>Practical use cases, Hands-on SDK examples, and real-world API workflows.</CardDescription>
          </div>
        </CardBase>
      </BottomRow>
    </ProductsHoverContainer>
  );
};

export default ProductsHover;
